import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  getFirestore, collection, getDocs, addDoc, updateDoc,
  deleteDoc, doc, serverTimestamp, orderBy, query,
} from 'firebase/firestore';
import { Chart } from 'react-google-charts';
import Modal from './Modal';
import CadastroDominio from './CadastroDominio';
import WhoisChecker from './WhoisChecker';

const AUTHORIZED_EMAILS = [
  'umdevbr@gmail.com',
  'fndo.xyz@gmail.com',
  'fcbertoo@gmail.com',
];

const db = getFirestore();

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --bg:          #080f1c;
    --bg2:         #0a1628;
    --bg-card:     #0f1e35;
    --bg-card-h:   #132440;
    --teal:        #00adb5;
    --teal-dim:    rgba(0,173,181,0.12);
    --teal-border: rgba(0,173,181,0.22);
    --accent:      #ff5722;
    --yellow:      #ffc107;
    --green:       #4caf50;
    --white:       #f0f4f8;
    --muted:       rgba(240,244,248,0.45);
    --tr:          0.2s cubic-bezier(0.4,0,0.2,1);
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .p-root {
    min-height: 100vh;
    background: linear-gradient(150deg,#0d2240 0%,#0a1628 40%,#080f1c 100%);
    font-family: 'DM Sans', sans-serif;
    color: var(--white);
  }

  /* ── Top bar ── */
  .p-topbar {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(10,22,40,0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--teal-border);
    padding: 0 28px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  .p-topbar__brand {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--teal);
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .p-topbar__brand-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: var(--teal);
    animation: pulse 2s ease-in-out infinite;
  }
  @keyframes pulse {
    0%,100% { opacity:1; transform:scale(1); }
    50%      { opacity:.4; transform:scale(.7); }
  }
  .p-topbar__right {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .p-topbar__user {
    font-size: 12.5px;
    color: var(--muted);
  }
  .p-topbar__logout {
    background: transparent;
    border: 1px solid rgba(255,87,34,0.35);
    border-radius: 7px;
    color: #ff7043;
    font-family: 'DM Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 500;
    padding: 6px 14px;
    cursor: pointer;
    transition: background var(--tr), border-color var(--tr);
  }
  .p-topbar__logout:hover { background: rgba(255,87,34,0.1); border-color: rgba(255,87,34,0.6); }

  /* ── Layout ── */
  .p-body {
    max-width: 1280px;
    margin: 0 auto;
    padding: 32px 28px 80px;
  }

  /* ── Tabs ── */
  .p-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 28px;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    padding-bottom: 0;
  }
  .p-tab {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    padding: 10px 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 500;
    color: var(--muted);
    cursor: pointer;
    transition: color var(--tr), border-color var(--tr);
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .p-tab:hover { color: var(--white); }
  .p-tab.active { color: var(--teal); border-bottom-color: var(--teal); }
  .p-tab__badge {
    background: var(--accent);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    border-radius: 10px;
    padding: 1px 6px;
    min-width: 18px;
    text-align: center;
  }

  /* ── Stats ── */
  .p-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 14px;
    margin-bottom: 28px;
  }
  .p-stat {
    background: var(--bg-card);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 12px;
    padding: 20px 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: border-color var(--tr);
  }
  .p-stat:hover { border-color: var(--teal-border); }
  .p-stat__dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    margin-bottom: 4px;
  }
  .p-stat__value {
    font-size: 28px;
    font-weight: 600;
    color: var(--white);
    line-height: 1;
  }
  .p-stat__label {
    font-size: 11.5px;
    color: var(--muted);
    letter-spacing: 0.04em;
  }

  /* ── Toolbar ── */
  .p-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }
  .p-search {
    flex: 1;
    min-width: 200px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px;
    padding: 9px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    color: var(--white);
    outline: none;
    transition: border-color var(--tr), background var(--tr);
  }
  .p-search::placeholder { color: rgba(240,244,248,0.2); }
  .p-search:focus { border-color: var(--teal-border); background: var(--teal-dim); }

  .p-btn {
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    padding: 9px 16px;
    cursor: pointer;
    transition: all var(--tr);
    white-space: nowrap;
  }
  .p-btn--primary { background: var(--teal); color: #fff; }
  .p-btn--primary:hover { background: #009aa2; box-shadow: 0 3px 12px rgba(0,173,181,0.35); }
  .p-btn--ghost {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.12);
    color: var(--muted);
  }
  .p-btn--ghost:hover { border-color: rgba(255,255,255,0.25); color: var(--white); }
  .p-btn--danger {
    background: rgba(255,87,34,0.1);
    border: 1px solid rgba(255,87,34,0.3);
    color: #ff7043;
  }
  .p-btn--danger:hover { background: rgba(255,87,34,0.2); }
  .p-btn--success {
    background: rgba(76,175,80,0.12);
    border: 1px solid rgba(76,175,80,0.3);
    color: #66bb6a;
  }
  .p-btn--success:hover { background: rgba(76,175,80,0.22); }
  .p-btn--sm { padding: 5px 11px; font-size: 12px; }

  /* ── Tabela ── */
  .p-table-wrap {
    background: var(--bg-card);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 12px;
    overflow: auto;
  }
  table.p-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }
  .p-table thead tr {
    border-bottom: 1px solid rgba(255,255,255,0.07);
  }
  .p-table th {
    padding: 13px 16px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
    cursor: pointer;
    white-space: nowrap;
    user-select: none;
    transition: color var(--tr);
  }
  .p-table th:hover { color: var(--white); }
  .p-table td {
    padding: 12px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    color: var(--white);
    vertical-align: middle;
  }
  .p-table tbody tr:last-child td { border-bottom: none; }
  .p-table tbody tr:hover td { background: rgba(255,255,255,0.02); }
  .p-table td.mono {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
  }
  .p-table td.muted { color: var(--muted); font-size: 12.5px; }

  /* Status badges */
  .p-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
  }
  .p-badge__dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
  .p-badge--analise  { background:rgba(255,193,7,.1);  border:1px solid rgba(255,193,7,.25);  color:#ffc107; }
  .p-badge--analise .p-badge__dot { background:#ffc107; animation: pulse 1.8s infinite; }
  .p-badge--publicado { background:rgba(0,173,181,.1); border:1px solid var(--teal-border); color:var(--teal); }
  .p-badge--publicado .p-badge__dot { background:var(--teal); }
  .p-badge--vendido  { background:rgba(76,175,80,.1);  border:1px solid rgba(76,175,80,.25);  color:#66bb6a; }
  .p-badge--vendido .p-badge__dot { background:#66bb6a; }
  .p-badge--reservado { background:rgba(33,150,243,.1); border:1px solid rgba(33,150,243,.25); color:#64b5f6; }
  .p-badge--reservado .p-badge__dot { background:#64b5f6; }
  .p-badge--default  { background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.1); color:var(--muted); }
  .p-badge--default .p-badge__dot { background:var(--muted); }

  /* ── Seção pendentes ── */
  .p-section-title {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .p-section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255,255,255,0.06);
  }

  /* Pendentes card */
  .p-pending-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 32px;
  }
  .p-pending-item {
    background: var(--bg-card);
    border: 1px solid rgba(255,193,7,0.18);
    border-radius: 12px;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: background var(--tr), border-color var(--tr);
    animation: fadeUp .3s ease both;
  }
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(6px); }
    to   { opacity:1; transform:translateY(0); }
  }
  .p-pending-item:hover { background: var(--bg-card-h); border-color: rgba(255,193,7,0.35); }
  .p-pending-item__icon {
    width: 38px; height: 38px;
    border-radius: 10px;
    background: rgba(255,193,7,0.1);
    border: 1px solid rgba(255,193,7,0.25);
    display: flex; align-items: center; justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
  }
  .p-pending-item__info { flex: 1; min-width: 0; }
  .p-pending-item__name {
    font-family: 'DM Mono', monospace;
    font-size: 14px;
    font-weight: 500;
    color: var(--white);
    margin-bottom: 3px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .p-pending-item__meta {
    font-size: 12px;
    color: var(--muted);
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
  .p-pending-item__meta span { display: flex; align-items: center; gap: 4px; }
  .p-pending-actions { display: flex; gap: 8px; flex-shrink: 0; }

  /* ── Feedback ── */
  .p-feedback {
    background: rgba(0,173,181,0.1);
    border: 1px solid var(--teal-border);
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 13px;
    color: var(--teal);
    margin-bottom: 20px;
    animation: fadeUp .3s ease;
  }

  /* ── Empty ── */
  .p-empty {
    padding: 48px 20px;
    text-align: center;
    color: var(--muted);
    font-size: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
  .p-empty__icon { font-size: 36px; opacity: .3; }

  /* ── Gráficos ── */
  .p-charts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    margin-top: 28px;
  }
  .p-chart-card {
    background: var(--bg-card);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 12px;
    padding: 20px;
  }
  .p-chart-card__title {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 16px;
  }

  /* ── Spinner ── */
  .p-spinner-wrap {
    display: flex; align-items: center; justify-content: center;
    padding: 80px 20px; gap: 14px;
    flex-direction: column;
  }
  .p-spinner {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 3px solid var(--teal-dim);
    border-top-color: var(--teal);
    animation: spin .9s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Footer total ── */
  .p-totals {
    display: flex;
    gap: 24px;
    padding: 14px 16px;
    border-top: 1px solid rgba(255,255,255,0.06);
    font-size: 12.5px;
    color: var(--muted);
    flex-wrap: wrap;
  }
  .p-totals strong { color: var(--white); }

  @media (max-width: 640px) {
    .p-body { padding: 20px 14px 60px; }
    .p-topbar { padding: 0 14px; }
    .p-tab { padding: 10px 12px; font-size: 12.5px; }
  }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_CLS = {
  'em análise': 'analise',
  'publicado':  'publicado',
  'vendido':    'vendido',
  'reservado':  'reservado',
  'consultar':  'publicado',
};
const getStatusCls = (s = '') => STATUS_CLS[s.toLowerCase().trim()] || 'default';
const getStatusLabel = (s = '') => s || 'Indefinido';

// ── Componente ────────────────────────────────────────────────────────────────
const Painel = () => {
  const auth = getAuth();

  const [user, setUser]             = useState(undefined); // undefined = carregando
  const [tab, setTab]               = useState('pendentes');
  const [dominios, setDominios]     = useState([]);
  const [clientes, setClientes]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [feedback, setFeedback]     = useState('');
  const [filtro, setFiltro]         = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('');
  const [colunaOrdenada, setColunaOrdenada] = useState('data');
  const [ordemAsc, setOrdemAsc]     = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [novoDominio, setNovoDominio] = useState({ nome:'', contato:'', titular:'', setor:'', valor:'' });
  const [editando, setEditando]     = useState(null);
  const [aprovando, setAprovando]   = useState(null);

  // ── Auth ──
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u && AUTHORIZED_EMAILS.includes(u.email)) setUser(u);
      else if (u) { signOut(auth); setUser(null); }
      else setUser(null);
    });
    return () => unsub();
  }, []);

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'dominios'));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDominios(data);

      const snapC = await getDocs(query(collection(db, 'clientes'), orderBy('data')));
      setClientes(snapC.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      showFeedback('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  const showFeedback = (msg) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 4000);
  };

  // ── Logout ──
  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = 'https://www.vedo.com.br';
  };

  // ── Aprovar domínio ──
  const handleAprovar = async (dominio) => {
    setAprovando(dominio.id);
    try {
      await updateDoc(doc(db, 'dominios', dominio.id), { status: 'Publicado' });
      setDominios((prev) => prev.map((d) => d.id === dominio.id ? { ...d, status: 'Publicado' } : d));
      showFeedback(`✅ "${dominio.nome}" publicado com sucesso.`);
    } catch (err) {
      console.error(err);
      showFeedback('Erro ao aprovar domínio.');
    } finally {
      setAprovando(null);
    }
  };

  // ── Excluir ──
  const handleExcluir = async (id, nome) => {
    if (!window.confirm(`Excluir "${nome}"? Esta ação é permanente.`)) return;
    try {
      await deleteDoc(doc(db, 'dominios', id));
      setDominios((prev) => prev.filter((d) => d.id !== id));
      showFeedback('Domínio excluído.');
    } catch (err) {
      showFeedback('Erro ao excluir.');
    }
  };

  // ── Editar ──
  const handleEditar = (dominio) => {
    setNovoDominio(dominio);
    setEditando(dominio);
    setMostrarForm(true);
  };

  // ── Salvar ──
  const handleSalvar = async (e) => {
    e.preventDefault();
    try {
      if (editando) {
        await updateDoc(doc(db, 'dominios', editando.id), { ...novoDominio, data: serverTimestamp() });
        setDominios((prev) => prev.map((d) => d.id === editando.id ? { ...d, ...novoDominio } : d));
        showFeedback('Domínio atualizado.');
      } else {
        const ref = await addDoc(collection(db, 'dominios'), { ...novoDominio, status: 'Publicado', data: serverTimestamp() });
        setDominios((prev) => [...prev, { ...novoDominio, id: ref.id, status: 'Publicado' }]);
        showFeedback('Domínio cadastrado.');
      }
      setNovoDominio({ nome:'', contato:'', titular:'', setor:'', valor:'' });
      setEditando(null);
      setMostrarForm(false);
    } catch (err) {
      showFeedback('Erro ao salvar.');
    }
  };

  // ── Ordenação ──
  const handleOrdenar = (col) => {
    if (colunaOrdenada === col) setOrdemAsc(!ordemAsc);
    else { setColunaOrdenada(col); setOrdemAsc(true); }
  };
  const thLabel = (col, label) =>
    `${label} ${colunaOrdenada === col ? (ordemAsc ? '↑' : '↓') : ''}`;

  // ── Dados derivados ──
  const pendentes    = dominios.filter((d) => d.status?.toLowerCase().trim() === 'em análise');
  const totalValor   = dominios.reduce((a, d) => a + (parseFloat(d.valor) || 0), 0);
  const totalSetores = new Set(dominios.map((d) => d.setor)).size;
  const totalOwners  = new Set(dominios.map((d) => d.contato)).size;

  const dominiosFiltrados = dominios
    .filter((d) => {
      if (!filtroAtivo) return true;
      const t = filtroAtivo.toLowerCase();
      return (
        d.nome?.toLowerCase().includes(t) ||
        d.titular?.toLowerCase().includes(t) ||
        d.contato?.toLowerCase().includes(t) ||
        d.setor?.toLowerCase().includes(t)
      );
    })
    .sort((a, b) => {
      const va = a[colunaOrdenada] ?? '';
      const vb = b[colunaOrdenada] ?? '';
      const cmp = va > vb ? 1 : va < vb ? -1 : 0;
      return ordemAsc ? cmp : -cmp;
    });

  // ── Gráficos ──
  const valorPorCliente = [['Cliente', 'Valor'], ...Object.entries(
    dominios.reduce((acc, d) => { acc[d.contato || '?'] = (acc[d.contato || '?'] || 0) + (parseFloat(d.valor) || 0); return acc; }, {})
  ).sort((a,b)=>b[1]-a[1]).slice(0,6)];

  const porSetor = [['Setor', 'Total'], ...Object.entries(
    dominios.reduce((acc, d) => { acc[d.setor || '?'] = (acc[d.setor || '?'] || 0) + 1; return acc; }, {})
  )];

  const chartOpts = {
    backgroundColor: 'transparent',
    legend: { textStyle: { color: '#8daab2', fontSize: 11 } },
    titleTextStyle: { color: '#f0f4f8', fontSize: 13 },
    pieSliceBorderColor: '#0f1e35',
    chartArea: { width: '90%', height: '80%' },
  };

  // ── Guards ──
  if (user === undefined) {
    return (
      <>
        <style>{styles}</style>
        <div className="p-root" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
          <div className="p-spinner-wrap">
            <div className="p-spinner" />
            <span style={{ color:'var(--muted)', fontSize:13 }}>Verificando acesso...</span>
          </div>
        </div>
      </>
    );
  }

  if (user === null) return <Navigate to="/Login" replace />;

  return (
    <>
      <style>{styles}</style>
      <div className="p-root">

        {/* Top bar */}
        <div className="p-topbar">
          <div className="p-topbar__brand">
            <span className="p-topbar__brand-dot" />
            VEDO · Painel
          </div>
          <div className="p-topbar__right">
            <span className="p-topbar__user">{user.email}</span>
            <button className="p-topbar__logout" onClick={handleLogout}>Sair</button>
          </div>
        </div>

        <div className="p-body">

          {/* Stats */}
          <div className="p-stats">
            {[
              { label:'Total domínios', value: dominios.length,  color:'#00adb5' },
              { label:'Em análise',     value: pendentes.length, color:'#ffc107' },
              { label:'Publicados',     value: dominios.filter(d=>d.status?.toLowerCase()==='publicado').length, color:'#00adb5' },
              { label:'Vendidos',       value: dominios.filter(d=>d.status?.toLowerCase()==='vendido').length,  color:'#4caf50' },
              { label:'Setores',        value: totalSetores,     color:'#64b5f6' },
              { label:'Anunciantes',    value: totalOwners,      color:'#ce93d8' },
              { label:'Valor total',    value: `R$ ${totalValor.toLocaleString('pt-BR',{minimumFractionDigits:0})}`, color:'#ffd54f' },
            ].map((s) => (
              <div className="p-stat" key={s.label}>
                <div className="p-stat__dot" style={{ background: s.color }} />
                <div className="p-stat__value">{s.value}</div>
                <div className="p-stat__label">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Feedback */}
          {feedback && <div className="p-feedback">{feedback}</div>}

          {/* Modal cadastro/edição */}
          {mostrarForm && (
            <Modal onClose={() => { setMostrarForm(false); setEditando(null); }}>
              <CadastroDominio
                novoDominio={novoDominio}
                handleChange={(e) => setNovoDominio(p => ({ ...p, [e.target.name]: e.target.value }))}
                handleSubmit={handleSalvar}
                registroEditando={editando}
              />
            </Modal>
          )}

          {/* Tabs */}
          <div className="p-tabs">
            <button className={`p-tab${tab==='pendentes'?' active':''}`} onClick={() => setTab('pendentes')}>
              Aguardando aprovação
              {pendentes.length > 0 && <span className="p-tab__badge">{pendentes.length}</span>}
            </button>
            <button className={`p-tab${tab==='dominios'?' active':''}`} onClick={() => setTab('dominios')}>
              Todos os domínios
            </button>
            <button className={`p-tab${tab==='graficos'?' active':''}`} onClick={() => setTab('graficos')}>
              Gráficos
            </button>
            <button className={`p-tab${tab==='whois'?' active':''}`} onClick={() => setTab('whois')}>
              Whois
            </button>
          </div>

          {/* ── Tab: Pendentes ── */}
          {tab === 'pendentes' && (
            loading ? (
              <div className="p-spinner-wrap"><div className="p-spinner" /></div>
            ) : pendentes.length === 0 ? (
              <div className="p-empty">
                <div className="p-empty__icon">✅</div>
                <p>Nenhum domínio aguardando aprovação.</p>
              </div>
            ) : (
              <>
                <p className="p-section-title">{pendentes.length} domínio{pendentes.length !== 1 ? 's' : ''} aguardando</p>
                <div className="p-pending-list">
                  {pendentes.map((d, i) => (
                    <div className="p-pending-item" key={d.id} style={{ animationDelay:`${i*40}ms` }}>
                      <div className="p-pending-item__icon">🕐</div>
                      <div className="p-pending-item__info">
                        <div className="p-pending-item__name">{d.nome}</div>
                        <div className="p-pending-item__meta">
                          <span>✉️ {d.contato || '—'}</span>
                          <span>👤 {d.titular || '—'}</span>
                          <span>📂 {d.setor || '—'}</span>
                          <span>📅 {formatDate(d.data)}</span>
                        </div>
                      </div>
                      <div className="p-pending-actions">
                        <button
                          className="p-btn p-btn--success p-btn--sm"
                          onClick={() => handleAprovar(d)}
                          disabled={aprovando === d.id}
                        >
                          {aprovando === d.id ? '...' : '✓ Publicar'}
                        </button>
                        <button
                          className="p-btn p-btn--danger p-btn--sm"
                          onClick={() => handleExcluir(d.id, d.nome)}
                        >
                          Recusar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          )}

          {/* ── Tab: Todos os domínios ── */}
          {tab === 'dominios' && (
            <>
              <div className="p-toolbar">
                <input
                  className="p-search"
                  type="text"
                  placeholder="Buscar por domínio, titular, contato ou setor..."
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && setFiltroAtivo(filtro)}
                />
                <button className="p-btn p-btn--primary" onClick={() => setFiltroAtivo(filtro)}>Buscar</button>
                {filtroAtivo && (
                  <button className="p-btn p-btn--ghost" onClick={() => { setFiltro(''); setFiltroAtivo(''); }}>
                    Limpar
                  </button>
                )}
                <button className="p-btn p-btn--primary" onClick={() => { setEditando(null); setNovoDominio({ nome:'', contato:'', titular:'', setor:'', valor:'' }); setMostrarForm(true); }}>
                  + Novo
                </button>
              </div>

              {loading ? (
                <div className="p-spinner-wrap"><div className="p-spinner" /></div>
              ) : (
                <div className="p-table-wrap">
                  <table className="p-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleOrdenar('nome')}>{thLabel('nome','Domínio')}</th>
                        <th onClick={() => handleOrdenar('contato')}>{thLabel('contato','Contato')}</th>
                        <th onClick={() => handleOrdenar('titular')}>{thLabel('titular','Titular')}</th>
                        <th onClick={() => handleOrdenar('setor')}>{thLabel('setor','Setor')}</th>
                        <th onClick={() => handleOrdenar('valor')}>{thLabel('valor','Valor')}</th>
                        <th onClick={() => handleOrdenar('status')}>{thLabel('status','Status')}</th>
                        <th onClick={() => handleOrdenar('data')}>{thLabel('data','Data')}</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dominiosFiltrados.length === 0 ? (
                        <tr><td colSpan={8} style={{ textAlign:'center', padding:'40px', color:'var(--muted)' }}>Nenhum resultado.</td></tr>
                      ) : dominiosFiltrados.map((d) => (
                        <tr key={d.id}>
                          <td className="mono">{d.nome}</td>
                          <td className="muted">{d.contato}</td>
                          <td>{d.titular}</td>
                          <td className="muted">{d.setor}</td>
                          <td>{d.valor ? `R$ ${Number(d.valor).toLocaleString('pt-BR')}` : '—'}</td>
                          <td>
                            <span className={`p-badge p-badge--${getStatusCls(d.status)}`}>
                              <span className="p-badge__dot" />
                              {getStatusLabel(d.status)}
                            </span>
                          </td>
                          <td className="muted">{formatDate(d.data)}</td>
                          <td>
                            <div style={{ display:'flex', gap:6 }}>
                              {d.status?.toLowerCase().trim() === 'em análise' && (
                                <button className="p-btn p-btn--success p-btn--sm" onClick={() => handleAprovar(d)} disabled={aprovando===d.id}>
                                  {aprovando===d.id ? '...' : 'Publicar'}
                                </button>
                              )}
                              <button className="p-btn p-btn--ghost p-btn--sm" onClick={() => handleEditar(d)}>Editar</button>
                              <button className="p-btn p-btn--danger p-btn--sm" onClick={() => handleExcluir(d.id, d.nome)}>Excluir</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="p-totals">
                    <span>Exibindo <strong>{dominiosFiltrados.length}</strong> de <strong>{dominios.length}</strong> domínios</span>
                    <span>Anunciantes: <strong>{totalOwners}</strong></span>
                    <span>Valor total: <strong>R$ {totalValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Tab: Gráficos ── */}
          {tab === 'graficos' && (
            <div className="p-charts">
              <div className="p-chart-card">
                <div className="p-chart-card__title">Top anunciantes por valor</div>
                <Chart chartType="PieChart" data={valorPorCliente} options={{ ...chartOpts, title:'' }} width="100%" height="260px" />
              </div>
              <div className="p-chart-card">
                <div className="p-chart-card__title">Distribuição por setor</div>
                <Chart chartType="PieChart" data={porSetor} options={{ ...chartOpts, title:'' }} width="100%" height="260px" />
              </div>
            </div>
          )}

          {/* ── Tab: Whois ── */}
          {tab === 'whois' && <WhoisChecker />}

        </div>
      </div>
    </>
  );
};

export default Painel;
